import { useLocation, useNavigate } from "react-router";

interface BackButtonProps {
  /**
   * Where back goes when there is no history to pop.
   *
   * A deep link, a refresh, or a parent arriving from a message all land on a
   * screen with an empty history stack, and `navigate(-1)` there either does
   * nothing or leaves the app entirely. Naming the parent screen means back
   * always goes somewhere inside the product.
   */
  to?: string | undefined;
  label?: string | undefined;
  className?: string | undefined;
}

/**
 * The one back control, so every screen's looks and behaves the same.
 *
 * The chevron matches the one `OnboardingLayout` already draws — onboarding
 * had a back button from the start and nothing else did, so half the product
 * had a way out and half did not.
 *
 * A 44px target: it sits in the top-left corner where a thumb reaches
 * awkwardly, and the negative margin keeps the icon aligned to the content
 * edge while the tappable area extends past it.
 */
export function BackButton({ to, label = "Go back", className = "" }: BackButtonProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    /*
     * `key` is "default" only on the first entry a router ever has, so this
     * is how we tell "somewhere to go back to" from "opened straight here".
     *
     * Read from the router rather than from `window.history`: the browser's
     * stack knows nothing about a memory router, and an index read off it
     * would make back behave differently in tests than in the app.
     */
    if (location.key !== "default") {
      void navigate(-1);
      return;
    }

    if (to) void navigate(to, { replace: true });
  };

  return (
    <button
      type="button"
      aria-label={label}
      onClick={goBack}
      className={`-m-3 flex h-11 w-11 items-center justify-center p-3 text-text ${className}`}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M15 5l-7 7 7 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
